/**
 * web-studio-sites D14 (studio-shop D8) — a plug-in's words, set per site.
 *
 * A plug-in may declare `siteOptions`: words a site can set for itself in
 * `site.json`'s `plugins` block, such as the shop's Telegram sign-in offer. The
 * vocabulary is small and closed: text with a length cap, a list with a least
 * and most count, a choice from a fixed set, and a group of those. Each option
 * carries its default, so the documentation and a site that sets nothing read
 * the same words.
 *
 * `pnpm site:publish` checks a site's block with `siteOptionProblem` and keeps
 * only what the site set (`SiteData.pluginOptions`); a piece reads an option
 * with `resolveSiteOption`, which fills anything unset from the default. Every
 * value is drawn as text, never as HTML. Pure and browser-safe.
 */

/** A value a site sets: text, a list, or a group of named values. */
export type SiteOptionValue = string | readonly SiteOptionValue[] | { readonly [field: string]: SiteOptionValue };

interface SiteOptionBase {
  /** What it is, for the plug-in's page and the kit's reference page. */
  readonly description: string;
  /** In a group, whether the field may be left out. */
  readonly optional?: true;
}

export interface SiteTextOption extends SiteOptionBase {
  readonly kind: 'text';
  /** At most this many characters, after trimming; never empty. */
  readonly maxLength: number;
}

export interface SiteChoiceOption extends SiteOptionBase {
  readonly kind: 'choice';
  /** The only values it takes (an icon from the set a drawing knows, for example). */
  readonly values: readonly string[];
}

export interface SiteListOption extends SiteOptionBase {
  readonly kind: 'list';
  readonly item: SiteOptionSpec;
  readonly min: number;
  readonly max: number;
}

export interface SiteGroupOption extends SiteOptionBase {
  readonly kind: 'group';
  readonly fields: Readonly<Record<string, SiteOptionSpec>>;
}

export type SiteOptionSpec = SiteTextOption | SiteChoiceOption | SiteListOption | SiteGroupOption;

/** A plug-in's option: its shape and its default, the words every site gets until it sets its own. */
export type SiteOption = SiteOptionSpec & { readonly default: SiteOptionValue };

const isGroupValue = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * Whether `key` is `record`'s own, not a name every object inherits (`toString`, `constructor`): how a site option or
 * a group field is looked up by name. Object.hasOwn's check, in a form the website's oldest browsers run.
 */
export const isOwn = (record: object, key: string): boolean => Object.prototype.hasOwnProperty.call(record, key);

/**
 * What is wrong with `value` as `spec` at `where` (`plugins.shop.signInOffer.heading`), or null when it fits.
 * `fill`: the option's own group, whose fields a site may leave out, since the default fills them.
 */
export function siteOptionProblem(spec: SiteOptionSpec, value: unknown, where: string, fill = false): string | null {
  switch (spec.kind) {
    case 'text': {
      if (typeof value !== 'string') return `${where} must be text`;
      const length = [...value.trim()].length;
      return length === 0 || length > spec.maxLength ? `${where} must be 1 to ${spec.maxLength} characters` : null;
    }
    case 'choice':
      return typeof value === 'string' && spec.values.includes(value) ? null : `${where} must be one of ${spec.values.join(', ')}`;
    case 'list': {
      if (!Array.isArray(value)) return `${where} must be a list`;
      if (value.length < spec.min || value.length > spec.max) return `${where} must have ${spec.min} to ${spec.max} items`;
      for (const [i, item] of value.entries()) {
        const problem = siteOptionProblem(spec.item, item, `${where}[${i}]`);
        if (problem) return problem;
      }
      return null;
    }
    case 'group': {
      if (!isGroupValue(value)) return `${where} must be an object of ${Object.keys(spec.fields).join(', ')}`;
      // Own names only: one every object inherits (constructor, toString) is never a field the group declares.
      for (const field of Object.keys(value)) {
        if (!isOwn(spec.fields, field)) return `${where}.${field} is not one of its fields: ${Object.keys(spec.fields).join(', ')}`;
      }
      for (const [field, fieldSpec] of Object.entries(spec.fields)) {
        const set = isOwn(value, field) ? value[field] : undefined;
        if (set === undefined) {
          if (fill || fieldSpec.optional) continue;
          return `${where}.${field} is needed`;
        }
        const problem = siteOptionProblem(fieldSpec, set, `${where}.${field}`);
        if (problem) return problem;
      }
      return null;
    }
    default:
      // A kind this check does not know accepts nothing.
      return `${where} is a kind of option this check does not know (${String((spec as { kind?: unknown }).kind)})`;
  }
}

/** Trims every text, so what a site set is stored as it is drawn. */
export function normalizeSiteOption(value: SiteOptionValue): SiteOptionValue {
  if (typeof value === 'string') return value.trim();
  if (Array.isArray(value)) return value.map(normalizeSiteOption);
  return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, normalizeSiteOption(v as SiteOptionValue)]));
}

/** The words to draw: what the site set, with a group's unset fields filled from the default; the default when unset. */
export function resolveSiteOption(option: SiteOption, set: SiteOptionValue | undefined): SiteOptionValue {
  if (set === undefined) return option.default;
  if (option.kind === 'group' && isGroupValue(set) && isGroupValue(option.default)) return { ...option.default, ...set };
  return set;
}
